import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * 將設計稿（designW x designH）縮放到容器寬度，背景圖與 overlay 使用同一個 scale。
 * - 以 ResizeObserver 量測容器寬度，計算高度（像素，避免百分比四捨五入造成位移）
 * - wrapper: overflow:hidden，避免 overlay 超出背景
 */
export default function StageFrame({
  bgImage,                // 背景圖（必填）
  designW = 750,          // 設計寬（px）
  designH = 1314,         // 設計高（px）
  maxPcWidth = 480,       // 桌面上限寬
  style,
  className,
  overlay = () => null,   // (ctx) => ReactNode；ctx: { px, scale, w, h }
}) {
  const wrapRef = useRef(null);
  const overlayRef = useRef(null);
  const [w, setW] = useState(0);

  // 量測容器寬度
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((ents) => {
      const width = Math.round(ents[0].contentRect.width);
      if (width !== w) setW(width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [w]);

  const scale = useMemo(() => (w ? w / designW : 0), [w, designW]);
  const h = useMemo(() => Math.round(scale * designH), [scale, designH]);
  
  // 計算實際需要的內容高度（根據 overlay 中的最高元素）
  const [contentHeight, setContentHeight] = useState(h);

  useEffect(() => {
    setContentHeight(h);
  }, [h]);
  
  useEffect(() => {
    if (!overlayRef.current || typeof ResizeObserver !== 'function') return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const height = entry.contentRect.height;
      const finalHeight = Math.max(h || 0, height || 0);
      setContentHeight(finalHeight);
    });
    ro.observe(overlayRef.current);
    return () => ro.disconnect();
  }, [h]);

  const effectiveHeight = contentHeight || h || 0;

  // px 轉換工具
  const px = (n) => Math.round(n * scale);

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: maxPcWidth,
        margin: '0 auto',
        // 用像素高度作為最小高度，允許內容超出時可捲動
        minHeight: h || undefined,
        // 實際高度根據內容動態調整
        height: effectiveHeight || 'auto',
        // 先給個比例，避免首次量測前高度為 0
        aspectRatio: h ? undefined : `${designW}/${designH}`,
        // 允許內容超出，由父容器處理滾動
        overflow: 'visible',
        ...style,
      }}
    >
      {/* 背景圖：等比鋪滿容器（容器本身已是設計比） */}
      {bgImage && (
        <img
          src={bgImage}
          alt=""
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: `${effectiveHeight}px`,
            objectFit: 'fill', // 容器即設計比；用 fill 完整貼合
            pointerEvents: 'none',
            userSelect: 'none',
          }}
          draggable={false}
        />
      )}

      {/* Overlay：與背景同尺寸的絕對層，允許內容超出 */}
      <div
        ref={overlayRef}
        className="stage-overlay"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          minHeight: `${effectiveHeight || h || 0}px`,
          // 移除 overflow: hidden，讓內容可以超出
          overflow: 'visible',
        }}
      >
        {overlay({ px, scale, w, h: effectiveHeight || h })}
      </div>
    </div>
  );
}
